/*
 * Every admin route, with the phase that builds it.
 *
 * This list and `ADMIN_NAV` describe the same set of pages from two directions —
 * one is what the sidebar shows, this is what the router serves. A nav item with
 * no route is a link to a 404, and `tests/unit/admin-nav.test.ts` asserts the
 * two agree.
 *
 * A row disappears from here when its real page lands.
 *
 * **Data only, no JSX, and that is why this is a `.ts`.** The server tsconfig
 * covers `tests/`, and it has neither `jsx` nor the DOM lib — deliberately, so a
 * `document` reference in server code fails to compile. A test importing a
 * `.tsx` breaks that build, so the element helper lives next door in
 * `PlaceholderPage.tsx` instead.
 */

/**
 * The highest phase that has shipped.
 *
 * **A placeholder naming a finished phase is a page nothing is going to build**,
 * and that is exactly what happened: Admins and Settings both said "Phase 3"
 * long after phase 3 closed, so the sidebar promised two pages the roadmap had
 * stopped scheduling. The owner spotted it on the screen, which is the wrong
 * place to find it.
 *
 * `tests/unit/admin-nav.test.ts` now refuses any phase at or below this number,
 * so closing a phase without moving its pages fails a test rather than shipping
 * a promise nobody is keeping. Bump it when a phase closes.
 */
export const LATEST_COMPLETE_PHASE = 12;

export interface AdminRoute {
  /** Path relative to /admin. The dashboard is the index route. */
  readonly path: string;
  readonly title: string;
  /**
   * `"Phase 9"`, or `null` for a page the roadmap does not schedule anywhere.
   *
   * `null` is not a tidier way of saying "later". It is the honest answer when
   * a page has no owner in the plan, and it renders differently — see
   * `PlaceholderPage`. Giving one of these a plausible-looking phase would hide
   * the thing worth knowing.
   */
  readonly phase: string | null;
  readonly summary: string;
}

/**
 * Admin pages that are actually built.
 *
 * A row moves here from `ADMIN_PLACEHOLDER_ROUTES` when its real page lands, and
 * `tests/unit/admin-nav.test.ts` asserts the two lists together cover the
 * sidebar exactly — no nav item without a route, no route the nav cannot reach,
 * and nothing in both lists at once.
 */
export const ADMIN_BUILT_ROUTES: readonly { path: string; title: string }[] = [
  { path: 'property-categories', title: 'Property Categories' },
  { path: 'services', title: 'Services' },
  { path: 'positions', title: 'Positions' },
  { path: 'employees', title: 'Employees' },
  { path: 'clients', title: 'Clients' },
  { path: 'jobs', title: 'Jobs' },
  { path: 'calendar', title: 'Calendar' },
  { path: 'job-requests', title: 'Job Requests' },
  { path: 'scheduling', title: 'Scheduling' },
  { path: 'recurring-jobs', title: 'Recurring Jobs' },
  { path: 'worker', title: 'Background Tasks' },
  { path: 'notification-emails', title: 'Notification Emails' },
  // Phase 9.10 and 9.8. Both forms went public before anything read what they
  // stored; these two rows moving is what closed that.
  { path: 'applicants', title: 'Applicants' },
  { path: 'leads', title: 'Leads' },
  // Phase 9A. The thread lives at conversations/:id too, which is a detail
  // route and so is not listed here — same as clients/:id and jobs/:id.
  { path: 'conversations', title: 'Conversations' },
  // Phase 10.1.
  { path: 'expenses', title: 'Expenses' },
  // Phase 10.2.
  { path: 'timesheets', title: 'Timesheets' },
  // Phase 10.4 and 10.5.
  { path: 'reports', title: 'Reports' },
  // Not a phase — the owner asked for it on 2026-08-10, after asking what the
  // dashboards actually tracked. The money was already in the reports; what was
  // missing was seeing it as a trend, and any picture of the website at all.
  { path: 'analytics', title: 'Analytics' },
  // Phase 11.6 and 11.7.
  { path: 'calendar-sync', title: 'Calendar Sync' },
  // Phase 12. The builder is invoices/:id, a detail route like jobs/:id.
  { path: 'invoices', title: 'Invoices' },
  /*
   * Not a phase — the owner asked for it on 2026-08-06, after the audit found
   * that an admin could not be created or revoked from inside the application
   * at all. It had been a placeholder since phase 2 with no phase owning it.
   */
  { path: 'admins', title: 'Admins' },
  // Also not a phase — the owner asked for it on 2026-08-06, right after the
  // Admins screen, and for the same reason: the values were SQL-only.
  { path: 'settings', title: 'Settings' },
];

export const ADMIN_PLACEHOLDER_ROUTES: readonly AdminRoute[] = [];
